import React, { useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { useLineItems } from '../../hooks/useLineItems';
import { useLocationAssignment } from '../../hooks/useLocationAssignment';
import { useOrderData } from '../../hooks/useOrderData';
import { useOrderSelection } from '../../hooks/useOrderSelection';
import { useLineItemSelection } from '../../hooks/useLineItemSelection';

const fulfillmentLocations = [
  {label: 'R001 - POV Sawgrass', value: 'R001 - POV Sawgrass'},
  {label: 'R002 - Just One Look', value: 'R002 - Just One Look'},
  {label: 'R003 - Eyes on Lincoln', value: 'R003 - Eyes on Lincoln'},
  {label: 'R004 - PV2 Dolphin', value: 'R004 - PV2 Dolphin'},
  {label: 'R005 - DE Mall of San Juan', value: 'R005 - DE Mall of San Juan'},
  {label: 'R006 - DE Brickell', value: 'R006 - DE Brickell'},
  {label: 'R008 - DE Florida Mall', value: 'R008 - DE Florida Mall'},
  {label: 'R009 - Aventura', value: 'R009 - Aventura'},
  {label: 'R010 - PV3 iDrive', value: 'R010 - PV3 iDrive'},
  {label: 'R011 - PV4 Vineland', value: 'R011 - PV4 Vineland'},
  {label: 'R014 - DE Plaza Las Americas', value: 'R014 - DE Plaza Las Americas'},
];

interface LineItemAssignmentDialogProps {
  orderData: ReturnType<typeof useOrderData>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemsHook: ReturnType<typeof useLineItems>;
  lineItemSelection: ReturnType<typeof useLineItemSelection>;
  locationAssignmentHook: ReturnType<typeof useLocationAssignment>;
}

export const LineItemAssignmentDialog: React.FC<LineItemAssignmentDialogProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  locationAssignmentHook,
  lineItemSelection
}) => {
  const toast = useRef<Toast>(null);
  const { selectedLineItem } = lineItemsHook;
  const {
    visible,
    pendingChange,
    selectedLocation,
    confirmLoading,
    error,
    inventoryState,
    hideDialog,
    setSelectedLocation,
    confirmLocationChange,
    clearError
  } = locationAssignmentHook;

  // Show error toast when error state changes
  useEffect(() => {
    if (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Assignment Error',
        detail: error,
        life: 5000,
      });
    }
  }, [error]);

  // Show insufficient inventory warning toast when location is selected
  useEffect(() => {
    if (selectedLocation && inventoryState.data && !inventoryState.loading) {
      const storeCode = selectedLocation.split(' ')[0];
      const selectedInventory = inventoryState.data.find(item => item.storeCode === storeCode);
      const currentLineItem = pendingChange || selectedLineItem;
      
      if (selectedInventory && !selectedInventory.hasEnoughInventory && currentLineItem) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Insufficient Inventory',
          detail: `Warning: This location has insufficient inventory (${selectedInventory.inventoryCount} available, ${currentLineItem.product_quantity} needed). The order may be delayed or declined by the store.`,
          life: 5000,
        });
      }
    }
  }, [selectedLocation, inventoryState.data, inventoryState.loading, pendingChange, selectedLineItem]);

  const handleConfirmAssignment = async () => {
    try {
      const response = await confirmLocationChange();
      
      if (response?.success === true) {
        toast.current?.show({
          severity: 'success',
          summary: 'Assignment Successful',
          detail: `${pendingChange?.product_name} has been assigned to ${selectedLocation}`,
          life: 3000,
        });
        lineItemSelection.clearSelection()
        lineItemsHook.refetch()
        orderData.refreshSingleOrder(orderSelection.selectedOrder?.order_id!)
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Assignment Failed',
        detail: error instanceof Error ? error.message : 'Failed to assign location. Please try again.',
        life: 5000,
      });
    }
  };

  const renderFooter = () => {
    return (
      <div>
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-text"
          onClick={hideDialog}
          disabled={confirmLoading}
        />
        <Button
          label={confirmLoading ? "Saving..." : "Confirm Assignment"}
          icon={confirmLoading ? "pi pi-spinner pi-spin" : "pi pi-check"}
          className="p-button-primary"
          onClick={handleConfirmAssignment}
          disabled={confirmLoading || !selectedLocation}
          autoFocus
        />
      </div>
    );
  };

  const currentLineItem = pendingChange || selectedLineItem;

  // Helper function to check if a store is currently selected
  const isStoreSelected = (storeName: string) => {
    return selectedLocation === storeName;
  };

  // Helper function to check if a store is currently assigned to the line item
  const isCurrentlyAssigned = (storeName: string) => {
    const storeCode = storeName.split(' ')[0];
    return currentLineItem?.fulfillment_location?.startsWith(storeCode);
  };

  // Helper function to get the appropriate styling classes
  const getStoreItemClasses = (storeName: string) => {
    const baseClasses = "inventory-list-item-dark d-flex justify-content-between align-items-center cursor-pointer";
    
    if (isStoreSelected(storeName)) {
      return `${baseClasses} bg-primary-50 border-primary border-2 shadow-1`;
    } else if (isCurrentlyAssigned(storeName)) {
      return `${baseClasses} bg-blue-50 border-left-3 border-primary`;
    } else {
      return baseClasses;
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        style={{ width: '600px' }}
        header="Assign Location to Line Item"
        modal
        footer={renderFooter()}
        onHide={hideDialog}
      >
        {currentLineItem && (
          <div>
            <div className="mb-4">
              <h6>Order: {currentLineItem.order_name}</h6>
              <p><strong>Product:</strong> {currentLineItem.product_name}</p>
              <p><strong>Quantity:</strong> {currentLineItem.product_quantity}</p>
              <p><strong>Current Assignment:</strong> {currentLineItem.fulfillment_location || 'Unassigned'}</p>
            </div>

            <div className="mb-4">
              <label htmlFor="newLocation" className="block mb-2">
                <strong>Assign to Location:</strong>
              </label>
              <Dropdown
                id="newLocation"
                value={selectedLocation}
                options={fulfillmentLocations.map(location => {
                  if (inventoryState.data) {
                    const storeCode = location.value.split(' ')[0];
                    const storeInventory = inventoryState.data.find(item => item.storeCode === storeCode);
                    
                    if (storeInventory) {
                      return {
                        ...location,
                        label: `${location.label} (${storeInventory.inventoryCount} in stock)`
                      };
                    }
                  }
                  return location;
                })}
                onChange={(e) => setSelectedLocation(e.value)}
                placeholder="Select Location"
                className="w-full"
                disabled={confirmLoading}
              />
            </div>

            <div className="mt-4">
              <h6>Inventory Availability</h6>
              {inventoryState.loading ? (
                <div className="text-center p-3">
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2">Loading inventory data...</p>
                </div>
              ) : inventoryState.error ? (
                <div className="p-3 border rounded bg-light text-danger">
                  <i className="pi pi-exclamation-circle me-2"></i>
                  {inventoryState.error}
                </div>
              ) : inventoryState.data ? (
                <div className="inventory-list-dark">
                  <div className="mb-3">
                    <h6>
                      <span className="text-success">
                        <i className="pi pi-check-circle me-2"></i>
                        Sufficient Inventory
                      </span>
                    </h6>
                    <ul className="inventory-list-group-dark">
                      {inventoryState.data
                        .filter(store => store.hasEnoughInventory)
                        .map(store => (
                          <li 
                            key={store.storeCode} 
                            className={getStoreItemClasses(store.storeName)}
                            onClick={() => setSelectedLocation(store.storeName)}
                          >
                            <span className="font-medium">{store.storeName}</span>
                            <Badge value={`${store.inventoryCount} in stock`} severity="success" />
                          </li>
                        ))}
                    </ul>
                  </div>
                  
                  {inventoryState.data.some(store => !store.hasEnoughInventory) && (
                    <div>
                      <h6>
                        <span className="text-warning">
                          <i className="pi pi-exclamation-triangle me-2"></i>
                          Insufficient Inventory
                        </span>
                      </h6>
                      <ul className="inventory-list-group-dark">
                        {inventoryState.data
                          .filter(store => !store.hasEnoughInventory)
                          .map(store => (
                            <li 
                              key={store.storeCode} 
                              className={getStoreItemClasses(store.storeName)}
                              onClick={() => setSelectedLocation(store.storeName)}
                            >
                              <span className="font-medium">{store.storeName}</span>
                              <Badge 
                                value={`${store.inventoryCount}/${currentLineItem.product_quantity} needed`} 
                                severity="warning" 
                              />
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};